// Which sign-in controls the claim page should offer, given what Firebase says
// about an email address.
//
// This is decided in two situations: on load for a buyer whose Stripe email
// already has a HiiKER account, and again whenever the buyer swaps the address
// (they mistyped it at checkout, or paid from a work address they don't want
// PRO+ on). Keeping it a pure function is what makes the second case safe to
// add — the branching is identical, only the trigger differs.
//
// The awkward case is an EMPTY methods array. It means one of two things and
// Firebase won't tell us which: no account exists, or the project has
// email-enumeration protection on and is refusing to say. So we never treat
// empty as proof of absence — we offer the password field either way and let
// the flow's existing `auth/email-already-in-use` → sign-in fallback sort it
// out, plus the email-link escape hatch for OAuth-only accounts.

export const SIGNUP_SUBMIT_LABEL = 'Activate my subscription';
export const SIGNIN_SUBMIT_LABEL = 'Log in & activate';

export interface ClaimAuthOptions {
  mode: 'signin' | 'signup';
  showPassword: boolean;
  showSubmit: boolean;
  showGoogle: boolean;
  showApple: boolean;
  showOauthDivider: boolean;
  showEmailLink: boolean;
  showForgot: boolean;
  submitLabel: string;
}

export function claimAuthOptionsFor(
  methods: string[],
  { emailChanged }: { emailChanged: boolean }
): ClaimAuthOptions {
  const unknown = methods.length === 0;
  const hasPassword = unknown || methods.includes('password');
  const showGoogle = methods.includes('google.com');
  const showApple = methods.includes('apple.com');

  // A known provider means the account exists, so we're signing in — even when
  // the only provider is OAuth and there's no password field to show. When
  // Firebase stayed silent we assume a fresh account for a swapped address, and
  // keep the server's "returning user" verdict for the original one.
  const mode: 'signin' | 'signup' = unknown && emailChanged ? 'signup' : 'signin';

  return {
    mode,
    showPassword: hasPassword,
    showSubmit: hasPassword,
    showGoogle,
    showApple,
    showOauthDivider: hasPassword && (showGoogle || showApple),
    showEmailLink: unknown,
    showForgot: hasPassword && mode === 'signin',
    submitLabel: mode === 'signup' ? SIGNUP_SUBMIT_LABEL : SIGNIN_SUBMIT_LABEL
  };
}
